import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { Layout } from '../../components/Layout.js';
import { Seo } from '../../components/Seo.js';
import { PricingSection } from './PricingSection.js';
import { ComparisonSection } from './ComparisonSection.js';
import { CoverageSection } from './CoverageSection.js';
import { HeroVisual } from './HeroVisual.js';
import {
  AnalyticsIcon,
  BoltIcon,
  CashIcon,
  MultiLangIcon,
  OfflineScanIcon,
  PayoutsIcon,
  RotatingQrIcon,
  SelfServeIcon,
  ShieldCheckIcon,
} from './FeatureIcons.js';
import { api } from '../../lib/api.js';
import type { PublicEvent, PublicEventsResponse } from '../discover/DiscoverPage.js';

export function LandingPage() {
  const { t } = useTranslation('landing');
  const { t: tCommon } = useTranslation('common');
  const { lang } = useParams();
  const locale = (lang as 'es' | 'en') ?? 'es';

  const featured = useQuery<PublicEventsResponse>({
    queryKey: ['landing', 'featured', locale],
    queryFn: () => api(`/events/public?page=1&pageSize=6&locale=${locale}`),
    refetchOnWindowFocus: false,
    staleTime: 60_000,
  });

  const hasEvents = (featured.data?.events.length ?? 0) > 0;

  const seoTitle = t('seo.title');
  const seoDescription = t('seo.description');

  const howSteps = [
    {
      title: t('how.steps.step1_title'),
      body: t('how.steps.step1_body'),
      icon: <BoltIcon className="w-6 h-6" />,
    },
    {
      title: t('how.steps.step2_title'),
      body: t('how.steps.step2_body'),
      icon: <SelfServeIcon className="w-6 h-6" />,
    },
    {
      title: t('how.steps.step3_title'),
      body: t('how.steps.step3_body'),
      icon: <ShieldCheckIcon className="w-6 h-6" />,
    },
  ];

  const features = [
    {
      icon: <RotatingQrIcon />,
      title: t('features.items.qr_title'),
      body: t('features.items.qr_body'),
    },
    {
      icon: <OfflineScanIcon />,
      title: t('features.items.scanner_title'),
      body: t('features.items.scanner_body'),
    },
    {
      icon: <PayoutsIcon />,
      title: t('features.items.payouts_title'),
      body: t('features.items.payouts_body'),
    },
    {
      icon: <SelfServeIcon />,
      title: t('features.items.selfserve_title'),
      body: t('features.items.selfserve_body'),
    },
    {
      icon: <AnalyticsIcon />,
      title: t('features.items.analytics_title'),
      body: t('features.items.analytics_body'),
    },
    {
      icon: <MultiLangIcon />,
      title: t('features.items.multilang_title'),
      body: t('features.items.multilang_body'),
    },
    {
      icon: <CashIcon />,
      title: t('features.items.credits_title'),
      body: t('features.items.credits_body'),
    },
    {
      icon: <ShieldCheckIcon />,
      title: t('features.items.antipiracy_title'),
      body: t('features.items.antipiracy_body'),
    },
    {
      icon: <BoltIcon />,
      title: t('features.items.mobile_title'),
      body: t('features.items.mobile_body'),
    },
  ];

  const faqs = [
    { q: t('faq.q1_q'), a: t('faq.q1_a') },
    { q: t('faq.q2_q'), a: t('faq.q2_a') },
    { q: t('faq.q3_q'), a: t('faq.q3_a') },
    { q: t('faq.q4_q'), a: t('faq.q4_a') },
    { q: t('faq.q5_q'), a: t('faq.q5_a') },
    { q: t('faq.q6_q'), a: t('faq.q6_a') },
    { q: t('faq.q7_q'), a: t('faq.q7_a') },
    { q: t('faq.q8_q'), a: t('faq.q8_a') },
  ];

  // SEO: Organization, WebSite, SoftwareApplication, HowTo and FAQPage.
  const organizationLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Boletra',
    url: 'https://boletra.com',
    logo: 'https://boletra.com/logo-full.png',
    sameAs: ['https://indiefy.net'],
  };
  const websiteLd = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'Boletra',
    url: 'https://boletra.com',
    potentialAction: {
      '@type': 'SearchAction',
      target: `https://boletra.com/${locale}/discover?q={search_term_string}`,
      'query-input': 'required name=search_term_string',
    },
  };
  const productLd = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'Boletra',
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Web, iOS, Android',
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'USD',
      description: t('seo.product_offer_description'),
    },
  };
  const howToLd = {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name: t('seo.howto_name'),
    description: seoDescription,
    step: howSteps.map((s, idx) => ({
      '@type': 'HowToStep',
      position: idx + 1,
      name: s.title,
      text: s.body,
    })),
  };
  const faqLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((q) => ({
      '@type': 'Question',
      name: q.q,
      acceptedAnswer: { '@type': 'Answer', text: q.a },
    })),
  };

  return (
    <Layout>
      <Seo
        title={seoTitle}
        description={seoDescription}
        ogType="website"
        locale={locale}
        jsonLd={[organizationLd, websiteLd, productLd, howToLd, faqLd]}
      />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <Backdrop />
        <div className="relative max-w-6xl mx-auto px-4 pt-12 sm:pt-20 pb-16 sm:pb-24 grid gap-10 lg:gap-16 lg:grid-cols-[1.1fr_0.9fr] items-center">
          <div>
            <p className="inline-flex items-center gap-2 text-xs sm:text-sm uppercase tracking-widest text-[var(--color-accent)] font-semibold mb-4 px-3 py-1 rounded-full border border-[var(--color-accent)]/30 bg-[var(--color-accent)]/5">
              <span className="relative flex w-2 h-2">
                <span className="absolute inline-flex w-full h-full rounded-full bg-[var(--color-accent)] opacity-75 animate-ping" />
                <span className="relative inline-flex w-2 h-2 rounded-full bg-[var(--color-accent)]" />
              </span>
              {tCommon('brand.tagline')}
            </p>
            <h1 className="text-4xl sm:text-5xl md:text-7xl font-bold tracking-tight leading-[1.05]">
              {t('hero.title_prefix')}
              <span className="text-[var(--color-accent)]">{t('hero.title_highlight')}</span>
              {t('hero.title_suffix')}
            </h1>
            <p className="mt-5 sm:mt-6 text-base sm:text-lg text-neutral-400 max-w-2xl leading-relaxed">
              {t('hero.subtitle')}
            </p>
            <div className="mt-7 sm:mt-8 flex flex-col sm:flex-row flex-wrap gap-3">
              <Link to={`/${lang}/auth/signup`} className="btn-primary w-full sm:w-auto">
                {t('hero.cta_signup')}
                <ArrowRight />
              </Link>
              <Link to={`/${lang}/discover`} className="btn-secondary w-full sm:w-auto">
                {t('hero.cta_browse')}
              </Link>
            </div>
            <ul className="mt-6 flex flex-wrap gap-x-5 gap-y-2 text-xs text-neutral-500">
              <li className="inline-flex items-center gap-1.5">
                <CheckTiny />
                {t('hero.checks.no_contracts')}
              </li>
              <li className="inline-flex items-center gap-1.5">
                <CheckTiny />
                {t('hero.checks.free_tickets')}
              </li>
              <li className="inline-flex items-center gap-1.5">
                <CheckTiny />
                {t('hero.checks.stripe_payouts')}
              </li>
            </ul>
          </div>
          <div className="order-first lg:order-none">
            <HeroVisual />
          </div>
        </div>

        {/* Trust strip */}
        <div className="relative max-w-6xl mx-auto px-4 pb-10">
          <div className="rounded-2xl border border-neutral-900 bg-neutral-950/40 px-4 sm:px-8 py-5 grid grid-cols-2 sm:grid-cols-4 gap-y-4 items-center">
            <TrustItem
              label={t('hero.trust.payouts_label')}
              sub="Stripe Connect"
              icon={<PayoutsIcon className="w-5 h-5" />}
            />
            <TrustItem
              label={t('hero.trust.antipiracy_label')}
              sub={t('hero.trust.antipiracy_sub')}
              icon={<ShieldCheckIcon className="w-5 h-5" />}
            />
            <TrustItem
              label={t('hero.trust.offline_label')}
              sub={t('hero.trust.offline_sub')}
              icon={<OfflineScanIcon className="w-5 h-5" />}
            />
            <TrustItem
              label={t('hero.trust.multilang_label')}
              sub="ES · EN"
              icon={<MultiLangIcon className="w-5 h-5" />}
            />
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section
        id="how-it-works"
        className="border-t border-neutral-900 py-14 sm:py-20 scroll-mt-20"
      >
        <div className="max-w-6xl mx-auto px-4">
          <SectionEyebrow>{t('how.eyebrow')}</SectionEyebrow>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight max-w-2xl">
            {t('how.title')}
          </h2>
          <p className="text-neutral-400 mt-3 max-w-2xl">{t('how.subtitle')}</p>
          <ol className="mt-10 grid gap-5 sm:gap-6 md:grid-cols-3">
            {howSteps.map((s, i) => (
              <li key={i} className="relative card overflow-hidden">
                <div
                  aria-hidden
                  className="absolute -top-12 -right-10 w-40 h-40 rounded-full opacity-10"
                  style={{ background: 'var(--color-accent)' }}
                />
                <div className="relative flex items-center gap-3 mb-4">
                  <span className="inline-flex items-center justify-center w-9 h-9 rounded-full border border-[var(--color-accent)]/40 bg-[var(--color-accent)]/10 text-[var(--color-accent)] font-bold">
                    {i + 1}
                  </span>
                  <span className="text-[var(--color-accent)]">{s.icon}</span>
                </div>
                <h3 className="font-semibold text-lg mb-2">{s.title}</h3>
                <p className="text-neutral-400 text-sm leading-relaxed">{s.body}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* UPCOMING EVENTS */}
      {hasEvents && (
        <section className="border-t border-neutral-900 py-12 sm:py-16 bg-neutral-900/20">
          <div className="max-w-6xl mx-auto px-4">
            <div className="flex items-end justify-between mb-6 sm:mb-8 gap-4">
              <div>
                <SectionEyebrow>{t('upcoming.eyebrow')}</SectionEyebrow>
                <h2 className="text-2xl sm:text-3xl font-bold">{t('upcoming.title')}</h2>
              </div>
              <Link
                to={`/${lang}/discover`}
                className="text-sm text-neutral-400 hover:text-neutral-50 whitespace-nowrap"
              >
                {t('upcoming.see_all')}
              </Link>
            </div>
            <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {featured.data!.events.slice(0, 6).map((e) => (
                <FeaturedEvent key={e.id} event={e} lang={lang ?? 'es'} locale={locale} />
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* FEATURES */}
      <section className="border-t border-neutral-900 py-14 sm:py-20">
        <div className="max-w-6xl mx-auto px-4">
          <SectionEyebrow>{t('features.eyebrow')}</SectionEyebrow>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight max-w-2xl">
            {t('features.title')}
          </h2>
          <p className="text-neutral-400 mt-3 max-w-2xl">{t('features.subtitle')}</p>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {features.map((f) => (
              <FeatureCard key={f.title} {...f} />
            ))}
          </div>
        </div>
      </section>

      {/* COVERAGE */}
      <CoverageSection />

      {/* PRICING */}
      <PricingSection lang={lang ?? 'es'} />

      {/* COMPARISON */}
      <ComparisonSection lang={lang ?? 'es'} />

      {/* FAQ */}
      <section className="border-t border-neutral-900 py-14 sm:py-20">
        <div className="max-w-3xl mx-auto px-4">
          <SectionEyebrow>FAQ</SectionEyebrow>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">{t('faq.title')}</h2>
          <div className="mt-8 divide-y divide-neutral-900 border-y border-neutral-900">
            {faqs.map((f, i) => (
              <FaqItem key={i} q={f.q} a={f.a} defaultOpen={i === 0} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative overflow-hidden border-t border-neutral-900 py-16 sm:py-24">
        <div
          aria-hidden
          className="absolute inset-0 opacity-30 pointer-events-none"
          style={{
            background:
              'radial-gradient(circle at 50% 0%, var(--color-accent) 0%, transparent 50%)',
          }}
        />
        <div className="relative max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-3xl sm:text-5xl font-bold tracking-tight mb-3 sm:mb-4">
            {t('cta.title')}
          </h2>
          <p className="text-neutral-400 mb-7 sm:mb-9 max-w-xl mx-auto">{t('cta.subtitle')}</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to={`/${lang}/auth/signup`} className="btn-primary w-full sm:w-auto">
              {t('cta.cta_signup')}
              <ArrowRight />
            </Link>
            <Link to={`/${lang}/discover`} className="btn-secondary w-full sm:w-auto">
              {t('cta.cta_browse')}
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
}

function Backdrop() {
  return (
    <div aria-hidden className="absolute inset-0 -z-0 pointer-events-none">
      <div
        className="absolute -top-32 left-1/2 -translate-x-1/2 w-[1100px] h-[700px] rounded-full opacity-25 blur-3xl"
        style={{
          background: 'radial-gradient(circle, rgba(196,255,62,0.45), transparent 60%)',
        }}
      />
      <svg className="absolute inset-0 w-full h-full opacity-[0.07]" aria-hidden>
        <defs>
          <pattern id="hero-grid" width="40" height="40" patternUnits="userSpaceOnUse">
            <path d="M 40 0 L 0 0 0 40" fill="none" stroke="#ffffff" strokeWidth="1" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#hero-grid)" />
      </svg>
    </div>
  );
}

function SectionEyebrow({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-xs uppercase tracking-widest text-[var(--color-accent)] font-semibold mb-2">
      {children}
    </p>
  );
}

function TrustItem({ label, sub, icon }: { label: string; sub: string; icon: React.ReactNode }) {
  return (
    <div className="flex items-center gap-3 px-3 sm:px-4 border-l border-neutral-900 first:border-l-0 sm:first:border-l-0 [&:nth-child(3)]:border-l-0 sm:[&:nth-child(3)]:border-l">
      <span className="text-[var(--color-accent)]">{icon}</span>
      <div className="leading-tight">
        <p className="text-sm font-semibold text-neutral-200">{label}</p>
        <p className="text-xs text-neutral-500">{sub}</p>
      </div>
    </div>
  );
}

function FeatureCard({
  icon,
  title,
  body,
}: {
  icon: React.ReactNode;
  title: string;
  body: string;
}) {
  return (
    <div className="card group hover:border-neutral-700 transition relative overflow-hidden">
      <div
        aria-hidden
        className="absolute -top-12 -right-12 w-32 h-32 rounded-full opacity-0 group-hover:opacity-10 transition"
        style={{ background: 'var(--color-accent)' }}
      />
      <div className="relative">
        <span className="inline-flex w-12 h-12 rounded-xl border border-neutral-800 bg-neutral-900/60 items-center justify-center mb-4">
          {icon}
        </span>
        <h3 className="font-semibold text-lg mb-2">{title}</h3>
        <p className="text-neutral-400 text-sm leading-relaxed">{body}</p>
      </div>
    </div>
  );
}

function FaqItem({ q, a, defaultOpen }: { q: string; a: string; defaultOpen?: boolean }) {
  const [open, setOpen] = useState(!!defaultOpen);
  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="w-full flex items-center justify-between text-left py-5 gap-4"
      >
        <span className="font-medium text-neutral-100">{q}</span>
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className={`flex-shrink-0 text-neutral-400 transition-transform ${open ? 'rotate-45 text-[var(--color-accent)]' : ''}`}
          aria-hidden
        >
          <line x1="12" y1="5" x2="12" y2="19" />
          <line x1="5" y1="12" x2="19" y2="12" />
        </svg>
      </button>
      <div
        className={['grid transition-all', open ? 'grid-rows-[1fr] pb-5' : 'grid-rows-[0fr]'].join(
          ' ',
        )}
      >
        <div className="overflow-hidden text-sm text-neutral-400 leading-relaxed">{a}</div>
      </div>
    </div>
  );
}

function FeaturedEvent({
  event,
  lang,
  locale,
}: {
  event: PublicEvent;
  lang: string;
  locale: 'es' | 'en';
}) {
  const fmtDate = new Intl.DateTimeFormat(locale, {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
  });
  const href = event.orgSlug ? `/${lang}/o/${event.orgSlug}/${event.slug}` : `/${lang}/discover`;
  return (
    <li className="card hover:border-neutral-700 transition">
      <Link to={href} className="block">
        <img
          src={event.bannerUrl ?? '/event-placeholder.jpg'}
          alt={event.title}
          loading="lazy"
          decoding="async"
          className="aspect-[16/9] object-cover rounded-lg w-full mb-4"
        />
        <p className="text-xs uppercase tracking-wider text-[var(--color-accent)] mb-1">
          {fmtDate.format(new Date(event.startsAt))}
        </p>
        <h3 className="font-semibold leading-tight line-clamp-2">{event.title}</h3>
        {event.venue && <p className="text-xs text-neutral-500 mt-1 truncate">{event.venue}</p>}
      </Link>
    </li>
  );
}

function CheckTiny() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="text-[var(--color-accent)]"
      aria-hidden
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function ArrowRight() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="ml-2"
      aria-hidden
    >
      <line x1="5" y1="12" x2="19" y2="12" />
      <polyline points="12 5 19 12 12 19" />
    </svg>
  );
}
